import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { logTransaction } from '../../common/log';
import { LogType } from '../../models/log.model';
import { IJwt } from '../../models/portal/auth.model';
import { setIsRefreshingTokenState, setTokenStoreAsync } from '../../store/authSlice';
import { postRefresh } from '../portal/auth.api';
import storeRef from '../../store';
import { authErrorStatic } from '../../providers/AuthErrorProvider';
import { fetchAppConfig } from '../../common/configUtil';
import axiosDmsInstance from './axiosDmsInstance';

interface IFailedQueue {
  resolve: (jwt: IJwt) => void;
  reject: (error: any) => void;
}

let isRefreshing = false;
let failedQueue: IFailedQueue[] = [];
let store: typeof storeRef | undefined;

const processFailedRequests = (jwt: IJwt) => {
  failedQueue.forEach((promise) => {
    promise.resolve(jwt);
  });
  failedQueue = [];
};

const handleRedirect = () => {
  logTransaction(LogType.Warn, `[AUTH] token was unable to be refreshed, displaying warning!`);
  authErrorStatic.showAuthErrorPub();
};

export const injectStore = (_store: typeof storeRef) => {
  store = _store;
};

// Request
export const dmsRequestInterceptorSuccess = async (config: InternalAxiosRequestConfig<any>) => {
  const state = store?.getState();
  const accessToken = state?.auth.token?.access_token;
  const database = state?.database;
  const registryAlias = database?.selectedDatabase?.registryAlias;

  config.headers = {
    ...config.headers,
    // Apply deployment type code
    DeploymentTypeCode: (await fetchAppConfig()).deploymentTypeCode || 'UNKNOWN_DEPLOYMENTTYPECODE',
  } as any;

  // Apply bearer token
  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `bearer ${accessToken}`,
    } as any;
  }

  // Apply database environment
  if (registryAlias) {
    config.headers = {
      ...config.headers,
      Environment: registryAlias,
    } as any;
  }

  return config;
};
export const dmsRequestInterceptorFail = (error: any) => {
  logTransaction(LogType.Error, `[HTTP_DMS_REQ_FAIL] ${error.response?.status} | Request failed for ${error.config?.url} | ${error}`);
  return Promise.reject(error);
};

// Response
export const dmsResponseInterceptorSuccess = (response: AxiosResponse<any, any>) => {
  return response;
};
export const dmsResponseInterceptorFail = (error: any) => {
  const currentJwt = store?.getState().auth.token;
  const refreshToken = currentJwt?.refresh_token || 'UNKNOWN_REFRESHTOKEN';
  const databaseId = store?.getState().database.selectedDatabase?.dbId || 'UNKNOWN_DATABASEID';

  const originalConfig = error.config;
  const errorMessage = error?.response?.data?.message;

  if (errorMessage) {
    logTransaction(LogType.Error, `[HTTP_DMS_RES_FAIL] ${error.response?.status || '0'} | Request failed for ${error.config?.url} | ${errorMessage || 'Unknown Network Error'}`);
  } else {
    logTransaction(LogType.Error, `[HTTP_DMS_RES_FAIL] ${error.response?.status || '0'} | Request failed for ${error.config?.url} | ${error.message || 'Unknown Network Error'}`);
  }

  if (error.response) {
    // Handle "Unauthorized" response message by generating new token
    if (error.response?.status === 401) {
      logTransaction(LogType.Warn, `[AUTH] detected 401 ERROR, refresh?  `);
      if (!originalConfig._retry) {
        if (isRefreshing) {
          logTransaction(LogType.Warn, `[AUTH] app waiting for token to refresh`);
          // Token is currently waiting for refresh
          return new Promise<IJwt>((resolve, reject) => {
            logTransaction(LogType.Warn, `[AUTH] request for ${error.config?.url?.substring(0, 20)}... queued waiting for refresh token`);
            failedQueue.push({ resolve, reject } as IFailedQueue);
          })
            .then((jwt: IJwt) => {
              originalConfig.headers = {
                ...originalConfig.headers,
                Authorization: `bearer ${jwt.access_token}`,
              };
              return axiosDmsInstance(originalConfig);
            })
            .catch((error) => {
              handleRedirect();
              return Promise.reject(error);
            });
        }

        logTransaction(LogType.Warn, `[AUTH] new token required`);

        originalConfig._retry = true;
        isRefreshing = true;
        store?.dispatch(setIsRefreshingTokenState(true));

        return new Promise(async (resolve, reject) => {
          try {
            logTransaction(LogType.Warn, `[AUTH] refreshing token`);
            let jwt = await postRefresh(refreshToken, databaseId.toString());

            if (jwt.error) {
              logTransaction(LogType.Error, `[AUTH] error refreshing token with ${refreshToken}. ${error}`);
              //handleRedirect();
              throw jwt.error;
            }

            authErrorStatic.clearCounterPub();
            const newJwt = { ...currentJwt, ...jwt };
            processFailedRequests(newJwt);
            await store?.dispatch(setTokenStoreAsync(newJwt));
            originalConfig.headers = {
              ...originalConfig.headers,
              Authorization: `bearer ${jwt.access_token}`,
            };
            resolve(axiosDmsInstance(originalConfig));
          } catch (err: any) {
            handleRedirect();
            reject(`Authentication error. ${err}`);
          } finally {
            isRefreshing = false;
            store?.dispatch(setIsRefreshingTokenState(false));
          }
        });
      }
      handleRedirect();
    }
  }

  return Promise.reject(error);
};
